/**
 * The single place the app builds a Quoflow method URL.
 *
 * Screens never call this — typed hooks in this folder do. Auth is the Frappe session cookie.
 * In dev that is same-origin (Vite proxies /api); in a static production deploy every URL is made
 * absolute via `apiBase()` and the call is cross-origin, so it also carries `X-Frappe-CSRF-Token`
 * on writes (`credentials: 'include'` throughout).
 *
 * Frappe wraps every success as {"message": payload} and reports failure in the body — sometimes
 * with a 200. Both are handled here so nothing downstream sees an envelope.
 *
 * MOCK-ONLY: every request also carries `X-Bosun-Role`, read from `./client.ts`'s
 * `getActingRole()` — the SAME acting-role state `SessionProvider.tsx` sets, shared across both
 * transports so a role switch affects every hook regardless of which one it calls. Production
 * Frappe derives the role from the session cookie and ignores this header; it is a dev
 * convenience for exercising the mock's role gates (e.g. `enquiry.assign_purchaser`,
 * `quotation.decide_quotation`), never a security boundary — see `./client.ts`'s own comment on
 * `actingRole` for the fuller version of this warning.
 */

import { apiBase } from './apiBase'
import { BosunApiError } from './errors'
import { getActingRole } from './client'
import { API_NS, assertOk, methodUrl } from './transport'
import { withSessionRetry } from './sessionRetry'
import { currentCsrfToken, proxyBearer } from './frappeSession'

// Re-exported so existing importers (and `bosun.test.ts`) keep working unchanged.
export { API_NS, methodUrl }

export type Params = Record<string, unknown>

/** Arrays and objects go over the wire as JSON; empty, null and undefined are dropped. */
function buildQuery(params?: Params): string {
  if (!params) return ''
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue
    search.set(key, typeof value === 'object' ? JSON.stringify(value) : String(value))
  }
  const qs = search.toString()
  return qs ? `?${qs}` : ''
}

async function sendOnce<T>(method: string, url: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = {}
  // A form (a file upload) sets its own multipart Content-Type, boundary included.
  const isForm = typeof FormData !== 'undefined' && body instanceof FormData
  if (body !== undefined && !isForm) headers['Content-Type'] = 'application/json'
  const actingRole = getActingRole()
  if (actingRole) headers['X-Bosun-Role'] = actingRole

  // Deployed, this is the whole proof of identity — `server/index.ts` verifies it and only then
  // forwards under the service token. `null` in dev, where the cookie authenticates instead; see
  // `currentClerkToken`.
  const bearer = await proxyBearer()
  if (bearer) headers['Authorization'] = `Bearer ${bearer}`

  if (method !== 'GET') {
    const csrf = currentCsrfToken()
    if (csrf) headers['X-Frappe-CSRF-Token'] = csrf
  }

  const res = await fetch(apiBase() + url, {
    method,
    credentials: 'include',
    headers,
    body: body === undefined ? undefined : isForm ? body : JSON.stringify(body),
  })

  let parsed: unknown = null
  try {
    parsed = await res.json()
  } catch {
    // A non-JSON body is only acceptable on an otherwise-successful empty response.
    if (!res.ok) throw new BosunApiError(res.status, undefined, [], `${res.status} ${res.statusText}`)
  }
  assertOk(res.status, parsed, res.statusText)
  return (parsed as { message: T } | null)?.message as T
}

/** Every `quoflow.*` call goes through here, so this is where an expired session is recovered —
 * see `./sessionRetry.ts`. Inert when Clerk is not configured. */
function send<T>(method: string, url: string, body?: unknown): Promise<T> {
  return withSessionRetry(() => sendOnce<T>(method, url, body))
}

export const callGet = <T>(module: string, method: string, params?: Params): Promise<T> =>
  send<T>('GET', methodUrl(module, method) + buildQuery(params))

export const callPost = <T>(module: string, method: string, body?: unknown): Promise<T> =>
  send<T>('POST', methodUrl(module, method), body ?? {})

/** `callPost` with a multipart form — a file upload. Same login, CSRF and session retry. */
export const callPostForm = <T>(module: string, method: string, form: FormData): Promise<T> =>
  send<T>('POST', methodUrl(module, method), form)

export const callPut = <T>(module: string, method: string, body?: unknown): Promise<T> =>
  send<T>('PUT', methodUrl(module, method), body ?? {})

export const callPatch = <T>(module: string, method: string, body?: unknown): Promise<T> =>
  send<T>('PATCH', methodUrl(module, method), body ?? {})

export const callDelete = <T>(module: string, method: string, body?: unknown): Promise<T> =>
  send<T>('DELETE', methodUrl(module, method), body ?? {})

/**
 * A POST to a whitelisted method OUTSIDE the `quoflow.*` namespace, by its full dotted name.
 * The one caller today is `uomSync.ts` (the connector's `pull_item_uom`); every such name is
 * also an explicit exemption in `server/policy.ts`, which denies the rest of the world.
 */
export const postDotted = <T>(dotted: string, body?: unknown): Promise<T> => send<T>('POST', `/api/method/${dotted}`, body ?? {})

